import FileHandler from '@tiptap/extension-file-handler';
import uploads from '../../services/uploads';
import app from '@/main';
import RsFormTipTap from '@/components/_global/RsFormTipTap.vue';
import { isImage } from '../../helpers';
import { editableImage } from '../consts';

/**
 * Upload method that simply returns a filename.
 * Wrapped to be used by multiple TipTap controls
 * @param file
 * @returns
 */
export const uploadEditorFile = async (file: File) => {
  const clientKey = app()?.$rsGlobals.$currentClient.clientKey;

  if (!clientKey) {
    throw new Error('No Client Key found to upload file with!');
  }

  const result = await uploads.uploadSingleFile(clientKey, 'userfiles', file);
  return result.filename;
};

/**
 * Opens a new dialog to select a file for uploading.
 * Uses callback functions to track upload state
 * @param onUploadStart
 * @param onUploadSuccess
 * @param onUploadError
 */
export const uploadWithFileDialog = async (config: {
  onUploadStart: () => Promise<void> | void,
  onUploadSuccess: (filename: string) => Promise<void> | void,
  onUploadError: () => Promise<void> | void,
  fileRestrict: 'image' | 'doc' | 'none',
}) => {
  const imageTypes = [ 'image/png', 'image/gif', 'image/jpeg', 'image/bmp', 'image/x-icon', 'image/webp' ];
  const docTypes = [ 'application/*', 'text/*' ];

  const fileTypes: Record<typeof config.fileRestrict, string> = {
    image: imageTypes.join(','),
    doc: docTypes.join(','),
    none: [ ...imageTypes, ...docTypes ].join(','),
  };

  const fileInput: HTMLInputElement = document.createElement('input');
  fileInput.setAttribute('type', 'file');
  fileInput.setAttribute('accept', fileTypes[config.fileRestrict]);
  fileInput.classList.add('ql-image');
  fileInput.addEventListener('change', (inputEvent: Event) => {
    const { files } = inputEvent.target as HTMLInputElement;

    if (files?.length) {
      const [ file ] = files;
      const reader = new FileReader();

      reader.onload = async () => {
        try {
          await config.onUploadStart();
          const result = await uploadEditorFile(file);
          await config.onUploadSuccess(result);
        } catch (err) {
          console.error('TIPTAP - Failed to upload via File Dialog', err);
          config.onUploadError();
        } finally {
          fileInput.value = '';
        }
      };

      reader.readAsDataURL(file);
    }
  });

  fileInput.click();
};

/**
 * Configured File Handler Extension for dropping and pasting files.
 * Takes a component param of type `RsFormTipTap` to set a `busy` state
 * while uploading. Accounts for image vs other file types when updating editor contents.
 * @param component RsFormTipTap instance
 * @returns
 */
export const FileHandlerExtension = (
  component: InstanceType<typeof RsFormTipTap>,
) => FileHandler.configure({
  onDrop: async (editor, files, pos) => {
    component.setBusy(true);

    try {
      const [ file ] = files;
      const upload = await uploadEditorFile(file);

      if (isImage(file)) {
        editor
          .chain()
          .insertContentAt(pos, {
            type: 'image',
            attrs: {
              ...editableImage,
              src: upload,
            },
          })
          .focus()
          .run();

        return;
      }

      editor
        .chain()
        .insertContentAt(pos, upload)
        .focus()
        .run();

      editor.chain().insertContent(' ').run();
    } catch (err) {
      console.error('TIPTAP: Drop upload failed', err);
    } finally {
      component.setBusy(false);
    }
  },

  onPaste: async (editor, files) => {
    component.setBusy(true);

    try {
      const [ file ] = files;
      const upload = await uploadEditorFile(file);

      if (isImage(file)) {
        editor
          .chain()
          .insertContentAt(editor.state.selection.anchor, {
            type: 'image',
            attrs: {
              ...editableImage,
              src: upload,
            },
          })
          .focus()
          .run();

        return;
      }

      editor
        .chain()
        .insertContentAt(editor.state.selection.anchor, upload)
        .focus()
        .run();

      editor.chain().insertContent(' ').run();
    } catch (err) {
      console.error('TIPTAP: Paste upload failed', err);
    } finally {
      component.setBusy(false);
    }
  },
});
